import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

let scannerSeq = 0;

const waitForElement = async (id, timeoutMs = 2500) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const element = document.getElementById(id);
    if (element && element.clientWidth > 0) return element;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  const element = document.getElementById(id);
  if (element) return element;
  throw new Error("SCANNER_VIEW_NOT_READY");
};

const stopScanner = async (scanner) => {
  if (!scanner) return;
  try {
    const state = typeof scanner.getState === "function" ? scanner.getState() : null;
    if (scanner.isScanning || state === 2 || state === 3) {
      await scanner.stop();
    }
  } catch (error) {
    // Camera may already be stopped.
  }
  try {
    scanner.clear();
  } catch (error) {
    // Ignore leftover DOM cleanup errors.
  }
};

const CameraScanDialog = ({ open, onClose, onScan }) => {
  const { t } = useTranslation();
  const scannerRef = useRef(null);
  const handlingRef = useRef(false);
  const onScanRef = useRef(onScan);
  const tRef = useRef(t);
  const elementIdRef = useRef(`pharmacy-camera-scanner-${++scannerSeq}`);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const elementId = elementIdRef.current;

    const startScanner = async () => {
      setError("");
      setStarting(true);
      handlingRef.current = false;
      try {
        await waitForElement(elementId);
        if (cancelled) return;
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import(
          "html5-qrcode"
        );
        if (cancelled) return;

        await stopScanner(scannerRef.current);
        if (cancelled) return;

        const scanner = new Html5Qrcode(elementId, {
          verbose: false,
          useBarCodeDetectorIfSupported: true,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
        });
        scannerRef.current = scanner;

        const config = {
          fps: 12,
          aspectRatio: 1.333,
          disableFlip: false,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const width = Math.max(
              50,
              Math.floor(Math.min(viewfinderWidth * 0.92, 420)),
            );
            const height = Math.max(
              50,
              Math.floor(Math.min(viewfinderHeight * 0.32, 160)),
            );
            return { width, height };
          },
        };

        const onDecoded = (decodedText) => {
          if (handlingRef.current || cancelled) return;
          handlingRef.current = true;
          if (typeof onScanRef.current === "function") {
            onScanRef.current(String(decodedText || "").trim());
          }
        };

        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) {
          await stopScanner(scanner);
          return;
        }
        if (!cameras?.length) {
          throw new Error(tRef.current("pharmacy.cameraScanFailed"));
        }
        const preferred =
          cameras.find((camera) =>
            /back|rear|environment|world/i.test(camera.label || ""),
          ) || cameras[0];

        try {
          await scanner.start(preferred.id, config, onDecoded);
        } catch (startError) {
          if (cancelled) return;
          if (cameras.length > 1 && preferred.id !== cameras[0].id) {
            await scanner.start(cameras[0].id, config, onDecoded);
          } else {
            throw startError;
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(tRef.current("pharmacy.cameraScanFailed"));
        }
        await stopScanner(scannerRef.current);
        scannerRef.current = null;
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      stopScanner(scanner);
    };
  }, [open]);

  const handleClose = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    await stopScanner(scanner);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      keepMounted={false}
    >
      <DialogTitle>{t("pharmacy.cameraScan")}</DialogTitle>
      <DialogContent sx={{ position: "relative" }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("pharmacy.cameraScanHint")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ position: "relative", minHeight: 280 }}>
          {starting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          <Box
            id={elementIdRef.current}
            sx={{
              width: "100%",
              minHeight: 280,
              overflow: "hidden",
              borderRadius: 2,
              bgcolor: "#0f172a",
              "& video": { width: "100%", borderRadius: 2 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("pharmacy.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraScanDialog;
